import { Injectable } from '@angular/core';
import { Registration, RegistrationEntrance, RegistrationExit } from 'app/model/registration';
import { Observable } from 'rxjs';
import { LoginService } from '../../login/login.service';
import { RegistrationRequestService } from './registration-request.service';

@Injectable({
  providedIn: 'root'
})
export class RegistrationTrackerNavbarService {
  private seconds: number = 0

  /**
  * Constructor
  *
  * @param {LoginService} loginService
  * @param {RegistrationRequestService} loginService
  * 
  */
  constructor(
    private loginService: LoginService,
    private registrationRequestService: RegistrationRequestService
  ) { }

  public checkIfEmployeeHasCheckedToday(): Observable<Registration[]> {
    return this.registrationRequestService.checkIfEmployeeHasCheckedInToday()
  }

  public finallyLastRegistration(employeeId: number): Observable<Registration> {
    return this.registrationRequestService.finallyLastRegistration(employeeId)
  }

  public setStartTime(seconds: number): void {
    this.seconds = seconds
  }

  public resetTime(): void {
    this.seconds = 0
  }

  public calculateTimer(): { seconds: string, minutes: string, hours: string } {
    return this.getFormattedTimeFromSeconds(this.seconds++)
  }

  public getFormattedTimeFromSeconds(startSeconds: number) {
    const seconds = startSeconds % 60
    const minutes = Math.floor((startSeconds % 3600) / 60)
    const hours = Math.floor(startSeconds / 3600)

    const time = {
      seconds: this.formatTime(seconds),
      minutes: this.formatTime(minutes),
      hours: this.formatTime(hours)
    }

    return time
  }

  private formatTime(time: number): string {
    return time.toString().padStart(2, '0')
  }

  public checkIfItHasLatest(registrations: Registration[]) {
    return registrations.some((registration) => registration.latest)
  }

  public checkIfEmployeeHasAnyCheckedUnfinished(registrations: Registration[]): Registration {
    return registrations?.find(item => item.end_date === null && item.end_hour === null)
  }

  public getSecondsHasPassedFromRegistrations(registrations: Registration[]): number {
    let totalSeconds = 0

    registrations.forEach(item => {
      totalSeconds +=
        this.getSecondsHasPassedFromDate(item.start_date, item.start_hour, item.end_date, item.end_hour)
    })

    return totalSeconds
  }

  public getSecondsHasPassedFromDate(startDate: string, startHour: string, endDate?: string, endHour?: string): number {
    const start = this.parseLocalDateTime(startDate, startHour)
    const end = endDate && endHour ? this.parseLocalDateTime(endDate, endHour) : new Date()

    const diference = end.getTime() - start.getTime()

    return Math.floor(diference / 1000)
  }

  private parseLocalDateTime(dateStr: string, timeStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number)
    const [h, min, s] = timeStr.split(':').map(Number)

    return new Date(y, m - 1, d, h, min, s)
  }

  private getUserAngetFromBrowser(): string {
    return navigator.userAgent
  }

  private getLocalDate(): string {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  private getLocalTime(): string {
    const now = new Date()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
  }

  private fixDecimals(number: number, count: number): number {
    return parseFloat(number.toFixed(count))
  }

  public startTimer(position: GeolocationPosition | null): Observable<RegistrationEntrance> {
    const userAgent = this.getUserAngetFromBrowser()

    const user = this.loginService.getUser()

    const latitude = position?.coords.latitude
    const longitude = position?.coords.longitude

    const registration: RegistrationEntrance = {
      employee_id: user.employee.id,
      start_latitude: latitude ? this.fixDecimals(latitude, 6) : null,
      start_longitude: longitude ? this.fixDecimals(longitude, 6) : null,
      start_user_agent: userAgent
    }

    return this.registrationRequestService.startRegistration(registration)
  }

  public stopTimer(registrationId: number, position: GeolocationPosition | null, latest: boolean): Observable<Registration> {
    const userAgent = this.getUserAngetFromBrowser()

    const latitude = position?.coords.latitude
    const longitude = position?.coords.longitude

    const registration: RegistrationExit = {
      end_latitude: latitude ? this.fixDecimals(latitude, 6) : null,
      end_longitude: longitude ? this.fixDecimals(longitude, 6) : null,
      end_user_agent: userAgent,
      latest: latest
    }

    return this.registrationRequestService.finallyRegistration(registrationId, registration)
  }
}
